import { InputLookup } from '@/features/inventory/components/inputs/InputLookup';
import { useState, useEffect, useRef, useCallback } from "react";
import { CustomerPicker } from "@/features/customers/components/CustomerPicker";
import { useOrdersByCustomerId } from "../../hooks/useOrders";
import { useNotification } from "@/shared/hooks/useNotification";
import { useConfirm } from "@/shared/contexts/confirm-context";
import { randomId } from "@/shared/utils/randomId";
import { UserSearchSelect } from "@/shared/components/UserSearchSelect";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DatePicker } from "@/components/ui/date-picker";
import { Textarea } from "@/components/ui/textarea";
import { Field, FieldLabel } from "@/components/ui/field";
import { Checkbox } from "@/components/ui/checkbox";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from "@/components/ui/command";
import { cn } from "@/lib/utils";
import { Check, ChevronsUpDown, Plus, X } from "lucide-react";
import { useCreateBatchSowingInstructions } from "../../hooks/useSowingInstructions";
import { useUnitsOfMeasure } from "@/shared/hooks/useUnitsOfMeasure";
import { useDepartments } from "@/shared/hooks/useDepartments";
import { useVarietyPricings } from "../../hooks/useVarietyPricings";
import { useActiveCropTypes } from "../../hooks/useCropTypes";
import { useCropVarietiesByCropType } from "../../hooks/useCropVarieties";
import { useCreateRequisition } from "@/features/inventory/hooks/useRequisition";
import type { OrderItem } from "../../services/ordersApi";
import { toNullableApiDate } from "@/lib/businessDate";
import { buildSowingInstructionItem, mapSowingFieldErrors, sowingQuantityLabel, suggestGerminationDate, applyGerminationSuggestion, validateSowingDates, type SowingInstructionForm } from "../../services/sowingInstructionRequest";
import { getFieldErrors, getErrorMessage } from "@/shared/utils/errorHandler";



const PURPOSE_CUSTOMER_ORDER = 1;
const PURPOSE_STOCK_PRODUCTION = 2;

const initialSowingForm: Omit<SowingInstructionForm, "orderItemId"> = {
    seedBatchId: "",
    purpose: PURPOSE_STOCK_PRODUCTION,
    seedSource: 0,
    cropTypeId: "",
    cropVarietyId: "",
    variety: "",
    varietyPricingId: "",
    quantityToSow: "",
    unit: 0,
    germinationChamberId: "",
    sowingLocation: "",
    scheduledSowingDate: "",
    expectedGerminationDate: "",
    assignedToUserId: "",
    specialInstructions: "",
    notes: "",
    plannedPlantingDate: "",
    productionStageRecommendation: "",
    requiredInputs: [],
};

type Mode = 'customer' | 'readyMade';

interface CreateSowingInstructionDialogProps {
    open?: boolean;
    onOpenChange?: (open: boolean) => void;
    initialOrderId?: string;
    initialCustomerId?: string;
    initialOrderItemId?: string;
}

interface SowingFormItemProps {
    itemId: string;
    errors?: Record<string, string[]>;
    orderItem?: OrderItem;
    form: SowingInstructionForm;
    isPending: boolean;
    onFormChange: <K extends keyof SowingInstructionForm>(itemId: string, field: K, value: SowingInstructionForm[K]) => void;
    onGerminationSuggestion: (itemId: string, suggestion: string | null) => void;
    onAddRequiredInput: (itemId: string) => void;
    onRemoveRequiredInput: (itemId: string, index: number) => void;
    onRequiredInputChange: <K extends keyof SowingInstructionForm["requiredInputs"][number]>(itemId: string, index: number, field: K, value: SowingInstructionForm["requiredInputs"][number][K]) => void;
}

const SowingFormItem = ({
    itemId,
    errors,
    orderItem,
    form,
    isPending,
    onFormChange,
    onGerminationSuggestion,
    onAddRequiredInput,
    onRemoveRequiredInput,
    onRequiredInputChange,
}: SowingFormItemProps) => {
    const [assignedToUserName, setAssignedToUserName] = useState("");
    const [cropTypeOpen, setCropTypeOpen] = useState(false);
    const [varietyOpen, setVarietyOpen] = useState(false);
    const [selectedInputLabels, setSelectedInputLabels] = useState<Record<string, string>>({});
    const unitsQuery = useUnitsOfMeasure();
    const varietyPricingsQuery = useVarietyPricings();
    const cropTypesQuery = useActiveCropTypes();
    const cropVarietiesQuery = useCropVarietiesByCropType(form.cropTypeId);

    const selectedCrop = cropTypesQuery.data?.find((crop) => crop.id === form.cropTypeId);
    const selectedPricing = varietyPricingsQuery.data?.find((pricing) => pricing.id === form.varietyPricingId);
    const selectedVariety = cropVarietiesQuery.data?.find((variety) =>
        variety.id === (form.cropVarietyId || selectedPricing?.cropVarietyId));
    // Wait for the existing lookups so a variety override is not skipped while loading.
    const lifecycleReady = !!selectedCrop && cropVarietiesQuery.isSuccess &&
        (!(form.cropVarietyId || selectedPricing?.cropVarietyId) || !!selectedVariety);
    const germinationDays = lifecycleReady
        ? selectedVariety?.sowingToGerminationDays ?? selectedCrop.sowingToGerminationDays
        : undefined;
    const hasGerminationDuration = germinationDays != null && Number.isSafeInteger(germinationDays) && germinationDays > 0;
    const suggestion = lifecycleReady && form.scheduledSowingDate
        ? suggestGerminationDate(form.scheduledSowingDate, selectedVariety?.sowingToGerminationDays, selectedCrop.sowingToGerminationDays)
        : null;

    useEffect(() => {
        if (!form.expectedGerminationDateManuallySet) onGerminationSuggestion(itemId, suggestion);
    }, [itemId, suggestion, onGerminationSuggestion, form.expectedGerminationDate, form.expectedGerminationDateManuallySet]);

    // Does the selected crop type have varieties?
    const cropTypeHasVarieties = (cropVarietiesQuery.data?.length ?? 0) > 0;

    // Available pricings filtered by variety or by crop type (no variety)
    const availablePricings = varietyPricingsQuery.data?.filter((vp) => {
        if (form.cropVarietyId) return vp.cropVarietyId === form.cropVarietyId;
        if (form.cropTypeId && !cropTypeHasVarieties)
            return vp.cropTypeId === form.cropTypeId && !vp.cropVarietyId;
        return false;
    }) || [];

    return (
        <div className="space-y-4 p-4 border rounded bg-background">
            <div className="flex items-center justify-between">
                <h4 className="font-semibold">
                    {orderItem
                        ? orderItem.isCompanySeedling
                            ? orderItem.variety || orderItem.productName || "—"
                            : `${orderItem.variety || "—"} (${orderItem.cropType || "—"})`
                        : `Sowing Instruction`}
                    {orderItem?.trayTypeName ? (
                        <span className="ml-2 text-xs font-normal text-muted-foreground">
                            ({orderItem.trayTypeName})
                        </span>
                    ) : orderItem?.pricingModeName ? (
                        <span className="ml-2 text-xs font-normal text-muted-foreground">
                            ({orderItem.pricingModeName})
                        </span>
                    ) : null}
                </h4>
            </div>

            {errors && <div role="alert" className="text-sm text-destructive">
                {Object.entries(errors).map(([field, messages]) => <p key={field}>{field.replace(/([A-Z])/g, " $1")}: {messages.join(" ")}</p>)}
            </div>}
            {/* Basic Info */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                <Field>
                    <FieldLabel>Crop Type <span className="text-red-500">*</span></FieldLabel>
                    <Popover open={cropTypeOpen} onOpenChange={setCropTypeOpen}>
                        <PopoverTrigger asChild>
                            <Button
                                variant="outline"
                                role="combobox"
                                aria-expanded={cropTypeOpen}
                                className="w-full justify-between font-normal"
                                disabled={isPending || cropTypesQuery.isLoading || !!orderItem}
                            >
                                {form.cropTypeId
                                    ? cropTypesQuery.data?.find((ct) => ct.id === form.cropTypeId)?.name ?? "Select Crop Type"
                                    : cropTypesQuery.isLoading
                                        ? "Loading crop types..."
                                        : "Select Crop Type"}
                                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                            </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                            <Command>
                                <CommandInput placeholder="Search crop type..." />
                                <CommandList>
                                    <CommandEmpty>No crop types found.</CommandEmpty>
                                    <CommandGroup>
                                        {cropTypesQuery.data?.map((cropType) => (
                                            <CommandItem
                                                key={cropType.id}
                                                value={cropType.name}
                                                onSelect={() => {
                                                    onFormChange(itemId, "cropTypeId", cropType.id);
                                                    onFormChange(itemId, "cropVarietyId", "");
                                                    onFormChange(itemId, "variety", "");
                                                    onFormChange(itemId, "varietyPricingId", "");
                                                    onFormChange(itemId, "trayType", undefined);
                                                    setCropTypeOpen(false);
                                                }}
                                            >
                                                <Check
                                                    className={cn(
                                                        "mr-2 h-4 w-4",
                                                        form.cropTypeId === cropType.id ? "opacity-100" : "opacity-0"
                                                    )}
                                                />
                                                {cropType.name}
                                            </CommandItem>
                                        ))}
                                    </CommandGroup>
                                </CommandList>
                            </Command>
                        </PopoverContent>
                    </Popover>
                </Field>

                {/* Variety — only when crop type has varieties */}
                {form.cropTypeId && cropTypeHasVarieties && (
                    <Field>
                        <FieldLabel>Variety <span className="text-red-500">*</span></FieldLabel>
                        {cropVarietiesQuery.isLoading ? (
                            <div className="h-10 bg-gray-200 rounded animate-pulse" />
                        ) : (
                            <Popover open={varietyOpen} onOpenChange={setVarietyOpen}>
                                <PopoverTrigger asChild>
                                    <Button
                                        variant="outline"
                                        role="combobox"
                                        aria-expanded={varietyOpen}
                                        className="w-full justify-between font-normal"
                                        disabled={isPending || !!orderItem}
                                    >
                                        {form.cropVarietyId
                                            ? cropVarietiesQuery.data?.find((v) => v.id === form.cropVarietyId)?.name ?? "Select Variety"
                                            : "Select Variety"}
                                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                                    <Command>
                                        <CommandInput placeholder="Search variety..." />
                                        <CommandList>
                                            <CommandEmpty>No varieties found.</CommandEmpty>
                                            <CommandGroup>
                                                {cropVarietiesQuery.data?.map((variety) => (
                                                    <CommandItem
                                                        key={variety.id}
                                                        value={variety.name}
                                                        onSelect={() => {
                                                            const pricings = varietyPricingsQuery.data?.filter(
                                                                (v) => v.cropVarietyId === variety.id
                                                            ) || [];
                                                            const autoSelected = pricings.length === 1 ? pricings[0] : undefined;
                                                            onFormChange(itemId, "cropVarietyId", variety.id);
                                                            onFormChange(itemId, "variety", variety.name || "");
                                                            onFormChange(itemId, "varietyPricingId", autoSelected?.id || "");
                                                            if (autoSelected) {
                                                                onFormChange(itemId, "trayType", autoSelected.trayType ?? undefined);
                                                                onFormChange(itemId, "unit", autoSelected.pricingMode === 2 ? 5 : (autoSelected.trayType ? 11 : 0));
                                                            }
                                                            setVarietyOpen(false);
                                                        }}
                                                    >
                                                        <Check
                                                            className={cn(
                                                                "mr-2 h-4 w-4",
                                                                form.cropVarietyId === variety.id ? "opacity-100" : "opacity-0"
                                                            )}
                                                        />
                                                        {variety.name}
                                                    </CommandItem>
                                                ))}
                                            </CommandGroup>
                                        </CommandList>
                                    </Command>
                                </PopoverContent>
                            </Popover>
                        )}
                    </Field>
                )}

                {/* Pricing Option — shown when variety is selected or crop has no varieties */}
                {form.cropTypeId && (form.cropVarietyId || !cropTypeHasVarieties) && (
                    <Field>
                        <FieldLabel>Pricing Option <span className="text-red-500">*</span></FieldLabel>
                        {varietyPricingsQuery.isLoading ? (
                            <div className="h-10 bg-gray-200 rounded animate-pulse" />
                        ) : (
                            <Select
                                value={form.varietyPricingId || ""}
                                onValueChange={(value) => {
                                    const selected = varietyPricingsQuery.data?.find(
                                        (v) => v.id === value
                                    );
                                    onFormChange(itemId, "varietyPricingId", value);
                                    if (!cropTypeHasVarieties) {
                                        onFormChange(itemId, "variety", selected?.varietyName || cropTypesQuery.data?.find((ct) => ct.id === form.cropTypeId)?.name || "");
                                    }
                                    if (selected) {
                                        onFormChange(itemId, "trayType", selected.trayType ?? undefined);
                                        onFormChange(itemId, "unit", selected.pricingMode === 2 ? 5 : (selected.trayType ? 11 : 0));
                                    }
                                }}
                                disabled={isPending || !!orderItem}
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder="Select pricing" />
                                </SelectTrigger>
                                <SelectContent>
                                    {availablePricings.map((vp) => {
                                        const priceLabel = vp.companySeedlingPrice != null
                                            ? `KES ${vp.companySeedlingPrice}`
                                            : (vp.propagationServicePrice != null ? `KES ${vp.propagationServicePrice} (propagation)` : "no price");
                                        return (
                                            <SelectItem key={vp.id} value={vp.id}>
                                                {vp.varietyName ? `${vp.varietyName} — ` : ""}
                                                {vp.pricingMode === 2
                                                    ? `Per Seedling/Piece — ${priceLabel}`
                                                    : `${vp.trayTypeName || vp.trayType + '-Hole'} — ${priceLabel}`}
                                            </SelectItem>
                                        );
                                    })}
                                </SelectContent>
                            </Select>
                        )}
                    </Field>
                )}

                <Field>
                    <FieldLabel>Purpose <span className="text-red-500">*</span></FieldLabel>
                    <Select
                        value={form.purpose.toString()}
                        onValueChange={(value) =>
                            onFormChange(itemId, "purpose", parseInt(value))
                        }
                        disabled={isPending || !!orderItem}
                    >
                        <SelectTrigger>
                            <SelectValue placeholder="Select Purpose" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value={PURPOSE_CUSTOMER_ORDER.toString()}>
                                Customer Order
                            </SelectItem>
                            <SelectItem value={PURPOSE_STOCK_PRODUCTION.toString()}>
                                Stock Production
                            </SelectItem>
                        </SelectContent>
                    </Select>
                </Field>
            </div>

            {/* Seed Info */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                <Field>
                    <FieldLabel>Seed Source <span className="text-red-500">*</span></FieldLabel>
                    <Select
                        value={form.seedSource.toString()}
                        onValueChange={(value) =>
                            onFormChange(itemId, "seedSource", parseInt(value))
                        }
                        disabled={isPending || !!orderItem}
                    >
                        <SelectTrigger>
                            <SelectValue placeholder="Select Source" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="1">Company Stock</SelectItem>
                            <SelectItem value="2">Customer Provided</SelectItem>
                        </SelectContent>
                    </Select>
                </Field>

                <Field>
                    <FieldLabel>
                        {sowingQuantityLabel(form.unit, unitsQuery.data?.find((u) => u.value === form.unit)?.name)} <span className="text-red-500">*</span>
                    </FieldLabel>
                    <Input
                        type="number"
                        step="0.01"
                        value={form.quantityToSow}
                        aria-invalid={!!errors?.quantityToSow}
                        onChange={(e) =>
                            onFormChange(itemId, "quantityToSow", e.target.value)
                        }
                        placeholder="0.00"
                        disabled={isPending}
                    />
                    {errors?.quantityToSow && <p className="text-sm text-destructive">{errors.quantityToSow.join(" ")}</p>}
                </Field>

                <Field>
                    <FieldLabel>Unit <span className="text-red-500">*</span></FieldLabel>
                    <Select
                        value={form.unit.toString()}
                        onValueChange={(value) =>
                            onFormChange(itemId, "unit", parseInt(value))
                        }
                        disabled={isPending || !!orderItem || !!form.trayType}
                    >
                        <SelectTrigger>
                            <SelectValue placeholder="Select Unit" />
                        </SelectTrigger>
                        <SelectContent>
                            {unitsQuery.data?.map((unit, uidx) => (
                                <SelectItem
                                    key={`unit-${itemId}-${unit.value}-${uidx}`}
                                    value={unit.value.toString()}
                                >
                                    {unit.name} ({unit.abbreviation})
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </Field>
            </div>

            {/* Production Recommendation */}
            {form.productionStageRecommendation && (
                <div className="p-3 rounded bg-blue-50 border border-blue-200">
                    <h5 className="font-semibold text-blue-900 mb-2">System Recommendation</h5>
                    <p className="text-sm text-blue-800 whitespace-pre-wrap">{form.productionStageRecommendation}</p>
                </div>
            )}

            {/* Dates */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                <Field>
                    <FieldLabel>
                        Scheduled Sowing Date <span className="text-red-500">*</span>
                    </FieldLabel>
                    <DatePicker
                        value={form.scheduledSowingDate}
                        onChange={(value) => onFormChange(itemId, "scheduledSowingDate", value)}
                        disabled={isPending}
                    />
                    {errors?.scheduledSowingDate && <p className="text-sm text-destructive">{errors.scheduledSowingDate.join(" ")}</p>}
                    {form.plannedPlantingDate && (
                        <p className="text-xs text-gray-500 mt-1">
                            Recommended: {new Date(form.plannedPlantingDate).toLocaleDateString()}
                        </p>
                    )}
                </Field>

                <Field>
                    <FieldLabel>Expected Germination Date <span className="text-red-500">*</span></FieldLabel>
                    <DatePicker value={form.expectedGerminationDate} required
                        onChange={(value) => onFormChange(itemId, "expectedGerminationDate", value)}
                        disabled={isPending} />
                    {hasGerminationDuration ? (
                        <p className="text-xs text-muted-foreground">Suggested from sowing date + {germinationDays} germination days. You can edit this date.</p>
                    ) : lifecycleReady ? (
                        <p className="text-sm text-destructive" role="alert">Germination duration is not configured. Enter an expected germination date.</p>
                    ) : form.cropTypeId ? (
                        <p className="text-xs text-muted-foreground">Enter a date manually while germination settings are unavailable.</p>
                    ) : null}
                    {form.expectedGerminationDate && <Button type="button" variant="ghost" disabled={isPending}
                        onClick={() => onFormChange(itemId, "expectedGerminationDate", "")}>Clear date</Button>}
                    {errors?.expectedGerminationDate && <p className="text-sm text-destructive">{errors.expectedGerminationDate.join(" ")}</p>}
                </Field>
                <UserSearchSelect
                    label="Assigned To"
                    disabled={isPending}
                    selectedUserId={form.assignedToUserId}
                    selectedUserName={assignedToUserName}
                    onSelect={(user) => {
                        if (user) {
                            onFormChange(itemId, "assignedToUserId", user.id);
                            setAssignedToUserName(`${user.firstName} ${user.otherNames}`);
                        } else {
                            onFormChange(itemId, "assignedToUserId", "");
                            setAssignedToUserName("");
                        }
                    }}
                />
            </div>

            {/* Instructions */}
            <div className="space-y-4">
                <Field>
                    <FieldLabel>Special Instructions</FieldLabel>
                    <Textarea
                        value={form.specialInstructions}
                        onChange={(e) =>
                            onFormChange(itemId, "specialInstructions", e.target.value)
                        }
                        placeholder="Any special care instructions"
                        disabled={isPending}
                    />
                </Field>

                <Field>
                    <FieldLabel>Notes</FieldLabel>
                    <Textarea
                        value={form.notes}
                        onChange={(e) =>
                            onFormChange(itemId, "notes", e.target.value)
                        }
                        placeholder="Additional notes"
                        disabled={isPending}
                    />
                </Field>
            </div>

            {/* Required Inputs */}
            <div>
                <div className="flex items-center justify-between mb-3">
                    <h5 className="font-medium">Required Inputs</h5>
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => onAddRequiredInput(itemId)}
                        disabled={isPending}
                    >
                        <Plus className="w-4 h-4 mr-2" />
                        Add Input
                    </Button>
                </div>

                {form.requiredInputs.length > 0 && (
                    <div className="space-y-3">
                        {form.requiredInputs.map((input, idx) => (
                            <div
                                key={`${itemId}-input-${idx}`}
                                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 p-3 border rounded bg-muted/30"
                            >
                                <Field>
                                    <FieldLabel>Input <span className="text-red-500">*</span></FieldLabel>
                                    <InputLookup
                                        value={input.inputId}
                                        selectedLabel={selectedInputLabels[input.inputId]}
                                        onSelect={(selectedInput) => {
                                            setSelectedInputLabels((previous) => ({ ...previous, [selectedInput.id]: `${selectedInput.inputCode} — ${selectedInput.inputName}` }));
                                            onRequiredInputChange(
                                                itemId,
                                                idx,
                                                "inputId",
                                                selectedInput.id
                                            );
                                            onRequiredInputChange(
                                                itemId,
                                                idx,
                                                "unit",
                                                Number(selectedInput.unitOfMeasure)
                                            );
                                        }}
                                        disabled={isPending}
                                    />
                                </Field>

                                <Field>
                                    <FieldLabel>Quantity <span className="text-red-500">*</span></FieldLabel>
                                    <Input
                                        type="number"
                                        step="0.01"
                                        value={input.quantityNeeded}
                                        onChange={(e) =>
                                            onRequiredInputChange(
                                                itemId,
                                                idx,
                                                "quantityNeeded",
                                                e.target.value
                                            )
                                        }
                                        placeholder="0.00"
                                        disabled={isPending}
                                    />
                                </Field>

                                <Field>
                                    <FieldLabel>Unit</FieldLabel>
                                    <div className="h-10 px-3 py-2 rounded-md border border-input bg-muted flex items-center text-sm">
                                        {input.unit ? (
                                            unitsQuery.data?.find(u => u.value === input.unit)?.abbreviation || "—"
                                        ) : (
                                            <span className="text-muted-foreground">Select input first</span>
                                        )}
                                    </div>
                                </Field>

                                <Field>
                                    <FieldLabel>Notes</FieldLabel>
                                    <div className="flex gap-2">
                                        <Input
                                            value={input.notes}
                                            onChange={(e) =>
                                                onRequiredInputChange(
                                                    itemId,
                                                    idx,
                                                    "notes",
                                                    e.target.value
                                                )
                                            }
                                            placeholder="Notes"
                                            disabled={isPending}
                                        />
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="sm"
                                            onClick={() =>
                                                onRemoveRequiredInput(itemId, idx)
                                            }
                                            disabled={isPending}
                                        >
                                            ✕
                                        </Button>
                                    </div>
                                </Field>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export const CreateSowingInstructionDialog = ({
    open: controlledOpen,
    onOpenChange: controlledOnOpenChange,
    initialOrderId = "",
    initialCustomerId = "",
    initialOrderItemId = "",
}: CreateSowingInstructionDialogProps = {}) => {
    const [internalOpen, setInternalOpen] = useState(false);
    const open = controlledOpen !== undefined ? controlledOpen : internalOpen;
    const setOpen = controlledOnOpenChange !== undefined ? controlledOnOpenChange : setInternalOpen;
    
    const [mode, setMode] = useState<Mode>(initialOrderId || initialCustomerId ? 'customer' : 'customer');
    const [selectedCustomerId, setSelectedCustomerId] = useState(initialCustomerId);
    const [selectedOrderId, setSelectedOrderId] = useState(initialOrderId);
    const [selectedOrderItemIds, setSelectedOrderItemIds] = useState<Set<string>>(
        new Set()
    );
    const autoSelectedItemRef = useRef<string>("");
    // Kept stable across a timeout/manual retry; rotated only after success/reset.
    const createIdempotencyKeyRef = useRef<string>(randomId());
    const [sowingForms, setSowingForms] = useState<
        Record<string, SowingInstructionForm>
    >({});
    const [rowErrors, setRowErrors] = useState<Record<string, Record<string, string[]>>>({});
    const notify = useNotification();
    const confirm = useConfirm();

    const ordersQuery = useOrdersByCustomerId(selectedCustomerId);
    const createBatchMutation = useCreateBatchSowingInstructions();
    const createRequisitionMutation = useCreateRequisition();
    const unitsQuery = useUnitsOfMeasure();
    const departmentsQuery = useDepartments();
    const cropTypesQuery = useActiveCropTypes();
    const varietyPricingsQuery = useVarietyPricings();

    const selectedOrder = ordersQuery.data?.find((o) => o.id === selectedOrderId);

    // Auto-select the initial order item once the order + lookup data are ready
    useEffect(() => {
        if (
            !initialOrderItemId ||
            !selectedOrder ||
            !cropTypesQuery.data?.length ||
            !varietyPricingsQuery.data?.length ||
            autoSelectedItemRef.current === initialOrderItemId
        ) return;
        autoSelectedItemRef.current = initialOrderItemId;
        handleSelectOrderItem(initialOrderItemId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialOrderItemId, selectedOrder, cropTypesQuery.data, varietyPricingsQuery.data]);

    function handleSelectOrderItem(itemId: string) {
        const newSelected = new Set(selectedOrderItemIds);
        if (newSelected.has(itemId)) {
            newSelected.delete(itemId);
            setSowingForms((previous) => {
                const next = { ...previous };
                delete next[itemId];
                return next;
            });
        } else {
            newSelected.add(itemId);
            const orderItem = selectedOrder?.orderItems?.find(
                (item) => item.id === itemId
            );

            if (orderItem) {
                // Find crop type ID by name
                const cropType = cropTypesQuery.data?.find(
                    (ct) => ct.name === orderItem.cropType
                );
                const cropTypeId = cropType?.id || "";

                // Find variety pricing by variety and cropTypeId
                const varietyPricing = varietyPricingsQuery.data?.find((vp) => vp.id === orderItem.varietyPricingId)
                    ?? varietyPricingsQuery.data?.find((vp) =>
                        vp.varietyName === orderItem.variety && vp.cropTypeId === cropTypeId &&
                        vp.pricingMode === orderItem.pricingMode &&
                        (vp.trayType ?? null) === (orderItem.trayType ?? null));
                const varietyPricingId = varietyPricing?.id || "";

                // Set seed source based on isCompanySeedling
                const seedSource = orderItem.isCompanySeedling ? 1 : 2;

                // Get the planned planting date and recommendation from the order
                const plannedPlantingDate = selectedOrder?.plannedPlantingDate
                    ? toNullableApiDate(selectedOrder.plannedPlantingDate) ?? ""
                    : "";
                const productionStageRecommendation = selectedOrder?.productionStageRecommendation || "";

                setSowingForms((prev) => ({
                    ...prev,
                    [itemId]: {
                        orderItemId: itemId,
                        seedBatchId: orderItem.seedBatchId || "",
                        purpose: PURPOSE_CUSTOMER_ORDER,
                        seedSource,
                        cropTypeId,
                        cropVarietyId: varietyPricing?.cropVarietyId || "",
                        variety: orderItem.variety || "",
                        varietyPricingId,
                        quantityToSow: orderItem.quantity.toString() || "",
                        unit: orderItem.unit || 0,
                        trayType: orderItem.trayType ?? undefined,
                        germinationChamberId: "",
                        sowingLocation: "",
                        scheduledSowingDate: plannedPlantingDate,
                        expectedGerminationDate: "",
                        assignedToUserId: "",
                        specialInstructions: "",
                        notes: orderItem.notes || "",
                        plannedPlantingDate,
                        productionStageRecommendation,
                        requiredInputs: [],
                    },
                }));
            } else {
                // Fallback if orderItem not found
                const plannedPlantingDate = selectedOrder?.plannedPlantingDate
                    ? toNullableApiDate(selectedOrder.plannedPlantingDate) ?? ""
                    : "";
                const productionStageRecommendation = selectedOrder?.productionStageRecommendation || "";

                setSowingForms((prev) => ({
                    ...prev,
                    [itemId]: {
                        orderItemId: itemId,
                        ...initialSowingForm,
                        purpose: PURPOSE_CUSTOMER_ORDER,
                        scheduledSowingDate: plannedPlantingDate,
                        plannedPlantingDate,
                        productionStageRecommendation,
                    },
                }));
            }
        }
        setSelectedOrderItemIds(newSelected);
    }

    const handleAddReadyMadeForm = () => {
        const newId = `ready-made-${Date.now()}`;
        setSowingForms((prev) => ({
            ...prev,
            [newId]: {
                orderItemId: null,
                ...initialSowingForm,
                purpose: PURPOSE_STOCK_PRODUCTION,
            },
        }));
        setSelectedOrderItemIds((prev) => new Set([...prev, newId]));
    };

    const handleRemoveReadyMadeForm = (itemId: string) => {
        const newSelected = new Set(selectedOrderItemIds);
        newSelected.delete(itemId);
        setSelectedOrderItemIds(newSelected);
        setSowingForms((previous) => {
            const next = { ...previous };
            delete next[itemId];
            return next;
        });
    };

    const handleGerminationSuggestion = useCallback((itemId: string, suggestion: string | null) => {
        if (suggestion) setRowErrors((previous) => {
            if (!previous[itemId]?.expectedGerminationDate) return previous;
            const remaining = { ...previous[itemId] };
            delete remaining.expectedGerminationDate;
            return { ...previous, [itemId]: remaining };
        });
        setSowingForms((previous) => {
            const form = previous[itemId];
            if (!form) return previous;
            const updated = applyGerminationSuggestion(form, suggestion);
            return updated === form ? previous : { ...previous, [itemId]: updated };
        });
    }, []);

    const handleFormChange = <K extends keyof SowingInstructionForm>(
        itemId: string,
        field: K,
        value: SowingInstructionForm[K]
    ) => {
        setRowErrors((prev) => ({ ...prev, [itemId]: {} }));
        setSowingForms((prev) => ({
            ...prev,
            [itemId]: {
                ...prev[itemId],
                [field]: value,
                ...(field === "expectedGerminationDate" ? { expectedGerminationDateManuallySet: true } : {}),
                ...(["cropTypeId", "cropVarietyId"].includes(field)
                    ? { expectedGerminationDate: "", expectedGerminationDateManuallySet: false } : {}),
            },
        }));
    };

    const handleAddRequiredInput = (itemId: string) => {
        setSowingForms((prev) => ({
            ...prev,
            [itemId]: {
                ...prev[itemId],
                requiredInputs: [
                    ...prev[itemId].requiredInputs,
                    { inputId: "", quantityNeeded: "", unit: 0, notes: "" },
                ],
            },
        }));
    };

    const handleRemoveRequiredInput = (itemId: string, index: number) => {
        setSowingForms((prev) => ({
            ...prev,
            [itemId]: {
                ...prev[itemId],
                requiredInputs: prev[itemId].requiredInputs.filter(
                    (_, i) => i !== index
                ),
            },
        }));
    };

    const handleRequiredInputChange = <K extends keyof SowingInstructionForm["requiredInputs"][number]>(
        itemId: string,
        index: number,
        field: K,
        value: SowingInstructionForm["requiredInputs"][number][K]
    ) => {
        setSowingForms((prev) => ({
            ...prev,
            [itemId]: {
                ...prev[itemId],
                requiredInputs: prev[itemId].requiredInputs.map((input, i) =>
                    i === index ? { ...input, [field]: value } : input
                ),
            },
        }));
    };

    const handleSubmit = () => {
        if (selectedOrderItemIds.size === 0) {
            notify.error("Please add at least one sowing instruction");
            return;
        }

        const dateErrors: Record<string, Record<string, string[]>> = {};
        for (const itemId of selectedOrderItemIds) {
            const form = sowingForms[itemId];
            if (!form) continue;
            const errors = validateSowingDates(form);
            if (!Number.isFinite(Number(form.quantityToSow)) || Number(form.quantityToSow) <= 0) {
                errors.quantityToSow = ["Quantity to sow must be greater than zero."];
            }
            if (Object.keys(errors).length) dateErrors[itemId] = errors;
        }
        setRowErrors(dateErrors);
        if (Object.keys(dateErrors).length) {
            notify.error("Please fix the fields shown on each instruction.");
            return;
        }

        // Validate all forms
        for (const itemId of selectedOrderItemIds) {
            const form = sowingForms[itemId];
            if (!form) {
                notify.error("Invalid form data");
                return;
            }

            // Check required fields
            if (!form.cropTypeId) {
                notify.error("Please select a crop type for all items");
                return;
            }
            if (!form.varietyPricingId) {
                notify.error("Please select a pricing option for all items");
                return;
            }
            if (!form.purpose) {
                notify.error("Please select a purpose for all items");
                return;
            }
            if (form.orderItemId && form.purpose !== PURPOSE_CUSTOMER_ORDER) {
                notify.error(
                    "Purpose must be Customer Order when Order Item is selected"
                );
                return;
            }
            if (!form.seedSource) {
                notify.error("Please select a seed source for all items");
                return;
            }
            if (!form.quantityToSow) {
                notify.error("Please enter a quantity to sow for all items");
                return;
            }
            if (!form.unit) {
                notify.error("Please select a unit for all items");
                return;
            }
            if (!form.scheduledSowingDate) {
                notify.error("Please select a scheduled sowing date for all items");
                return;
            }
            // Validate required inputs if any exist
            for (const input of form.requiredInputs) {
                if (!input.inputId) {
                    notify.error("Please select an input for all required inputs");
                    return;
                }
                if (!input.quantityNeeded) {
                    notify.error("Please enter a quantity for all required inputs");
                    return;
                }
            }
        }

        const items = Array.from(selectedOrderItemIds).map((itemId) => {
            const form = sowingForms[itemId];
            return buildSowingInstructionItem(form);
        });
        const submittedIds = Array.from(selectedOrderItemIds);

        confirm({
            title: "Create sowing instructions?",
            description: `${items.length} sowing instruction(s) will be created${mode === "customer" && selectedOrder ? ` for order ${selectedOrder.orderNumber}` : ""}.`,
            confirmLabel: "Create",
            onConfirm: () =>
                createBatchMutation
                    .mutateAsync({ items, idempotencyKey: createIdempotencyKeyRef.current })
                    .then((instructions) => {
                    instructions.forEach((instruction) => {
                        const formDataForItem = instruction.orderItemId
                            ? sowingForms[instruction.orderItemId]
                            : undefined;

                        if (
                            formDataForItem?.requiredInputs &&
                            formDataForItem.requiredInputs.length > 0
                        ) {
                            const unitName = unitsQuery.data?.find(
                                (u) => u.value === formDataForItem.unit
                            )?.name;

                            // Look up Germination department by code; the seeded
                            // departments use stable codes (GRM = Germination, SOW = Sowing).
                            const germinationDept = departmentsQuery.data?.find(
                                (d) => d.code === "GRM"
                            );
                            if (!germinationDept) {
                                notify.error(
                                    "Germination department not found — cannot create requisition."
                                );
                                return;
                            }

                            const requisitionPayload = {
                                requiredByDate: new Date(
                                    instruction.scheduledSowingDate
                                ).toISOString(),
                                purpose: "Sowing Preparation",
                                departmentId: germinationDept.id,
                                greenhouseId: instruction.greenhouseId || undefined,
                                fieldId: instruction.greenhouseBedId || undefined,
                                notes: `For sowing instruction ${instruction.id.slice(
                                    0,
                                    8
                                )}. ${instruction.notes || ""}`,
                                items: formDataForItem.requiredInputs.map((input) => ({
                                    inputId: input.inputId,
                                    quantityRequested: parseFloat(input.quantityNeeded),
                                    unitOfMeasure: unitName ?? "",
                                    orderId:
                                        mode === "customer"
                                            ? selectedOrder?.id ?? undefined
                                            : undefined,
                                    orderItemId: instruction.orderItemId ?? undefined,
                                    sowingInstructionId: instruction.id,
                                })),
                            };

                            createRequisitionMutation.mutate(requisitionPayload);
                        }
                    });

                    resetDialog();
                    notify.success("Sowing instructions created successfully!");
                    })
                    .catch((error: unknown) => {
                        setRowErrors(mapSowingFieldErrors(getFieldErrors(error), submittedIds));
                        notify.error(getErrorMessage(error) || "Failed to create instructions");
                        throw error;
                    }),
        });
    };

    const resetDialog = () => {
        createIdempotencyKeyRef.current = randomId();
        setMode('customer');
        setSelectedCustomerId("");
        setSelectedOrderId("");
        setSelectedOrderItemIds(new Set());
        setSowingForms({});
        setRowErrors({});
        setOpen(false);
    };

    const handleDialogOpenChange = (nextOpen: boolean) => {
        if (nextOpen) {
            setSelectedCustomerId(initialCustomerId);
            setSelectedOrderId(initialOrderId);
        } else {
            autoSelectedItemRef.current = "";
        }
        setOpen(nextOpen);
    };

    return (
        <Dialog open={open} onOpenChange={handleDialogOpenChange}>
            <DialogTrigger asChild>
                <Button className="gap-2">
                    <Plus className="w-4 h-4" />
                    New Sowing Instruction
                </Button>
            </DialogTrigger>
            <DialogContent
                style={{ width: "90vw", maxWidth: "1600px" }}
                className="max-h-[95vh] overflow-y-auto"
            >
                <DialogHeader>
                    <DialogTitle>Create Sowing Instructions</DialogTitle>
                    <DialogDescription>
                        Create sowing instructions for customer orders or
                        company stock
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-6">
                    {/* Mode Selector */}
                    <div>
                        <h3 className="text-lg font-semibold mb-4">Mode</h3>
                        <div className="flex gap-4">
                            <Button
                                variant={
                                    mode === "customer" ? "default" : "outline"
                                }
                                onClick={() => {
                                    setMode("customer");
                                    setSelectedOrderItemIds(new Set());
                                    setSowingForms({});
                                }}
                                disabled={createBatchMutation.isPending || !!initialOrderId}
                            >
                                Customer Order
                            </Button>
                            <Button
                                variant={
                                    mode === "readyMade" ? "default" : "outline"
                                }
                                onClick={() => {
                                    setMode("readyMade");
                                    setSelectedOrderItemIds(new Set());
                                    setSowingForms({});
                                }}
                                disabled={createBatchMutation.isPending || !!initialOrderId}
                            >
                                Company Stock
                            </Button>
                        </div>
                    </div>

                    {/* Customer Mode */}
                    {mode === "customer" && (
                        <>
                            {/* Customer Selection */}
                            <div>
                                <h3 className="text-lg font-semibold mb-4">
                                    Customer & Order
                                </h3>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    <Field>
                                        <FieldLabel htmlFor="customerId">
                                            Customer{" "}
                                            <span className="text-red-500">*</span>
                                        </FieldLabel>
                                        <CustomerPicker
                                            value={selectedCustomerId}
                                            onChange={(value) => {
                                                setSelectedCustomerId(value);
                                                setSelectedOrderId("");
                                                setSelectedOrderItemIds(
                                                    new Set()
                                                );
                                                setSowingForms({});
                                            }}
                                            disabled={
                                                createBatchMutation.isPending
                                            }
                                            title="Customer"
                                        />
                                    </Field>

                                    {selectedCustomerId && (
                                        <Field>
                                            <FieldLabel htmlFor="orderId">
                                                Order{" "}
                                                <span className="text-red-500">
                                                    *
                                                </span>
                                            </FieldLabel>
                                            <Select
                                                value={selectedOrderId}
                                                onValueChange={(value) => {
                                                    setSelectedOrderId(value);
                                                    setSelectedOrderItemIds(
                                                        new Set()
                                                    );
                                                    setSowingForms({});
                                                }}
                                                disabled={
                                                    createBatchMutation.isPending ||
                                                    ordersQuery.isLoading
                                                }
                                            >
                                                <SelectTrigger
                                                    id="orderId"
                                                    className="w-full"
                                                >
                                                    <SelectValue
                                                        placeholder={
                                                            ordersQuery.isLoading
                                                                ? "Loading orders..."
                                                                : "Select Order"
                                                        }
                                                    />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {ordersQuery.data?.map(
                                                        (order) => (
                                                            <SelectItem
                                                                key={order.id}
                                                                value={order.id}
                                                            >
                                                                {order.orderNumber}{" "}
                                                                -{" "}
                                                                {new Date(
                                                                    order.expectedDeliveryDate
                                                                ).toLocaleDateString()}
                                                            </SelectItem>
                                                        )
                                                    )}
                                                </SelectContent>
                                            </Select>
                                        </Field>
                                    )}
                                </div>
                            </div>

                            {/* Order Items Selection */}
                            {selectedOrder && (
                                <div>
                                    <h3 className="text-lg font-semibold mb-4">
                                        Select Order Items
                                    </h3>
                                    <div className="rounded border overflow-hidden">
                                        <Table>
                                            <TableHeader>
                                                <TableRow>
                                                    <TableHead className="w-10">
                                                        { }
                                                    </TableHead>
                                                    <TableHead>Type</TableHead>
                                                    <TableHead>
                                                        Details
                                                    </TableHead>
                                                    <TableHead>
                                                        Pricing
                                                    </TableHead>
                                                    <TableHead>
                                                        Quantity
                                                    </TableHead>
                                                </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                                {selectedOrder.orderItems?.map(
                                                    (item, idx) => (
                                                        <TableRow
                                                            key={`${item.id}-${idx}`}
                                                        >
                                                            <TableCell>
                                                                <Checkbox
                                                                    checked={selectedOrderItemIds.has(
                                                                        item.id
                                                                    )}
                                                                    onCheckedChange={() =>
                                                                        handleSelectOrderItem(
                                                                            item.id
                                                                        )
                                                                    }
                                                                    disabled={
                                                                        createBatchMutation.isPending
                                                                    }
                                                                />
                                                            </TableCell>
                                                            <TableCell className="text-sm">
                                                                {item.isCompanySeedling
                                                                    ? "Company"
                                                                    : "Customer"}
                                                            </TableCell>
                                                            <TableCell className="text-sm">
                                                                {item.isCompanySeedling
                                                                    ? item.variety ||
                                                                    item.productName ||
                                                                    "—"
                                                                    : `${item.variety ||
                                                                    "—"
                                                                    } (${item.cropType ||
                                                                    "—"
                                                                    })`}
                                                            </TableCell>
                                                            <TableCell className="text-sm">
                                                                {item.pricingModeName === "Per Seedling/Piece" || item.pricingMode === 2
                                                                    ? "Per Seedling/Piece"
                                                                    : item.trayTypeName || (item.trayType === 210 ? "210-Hole" : item.trayType === 442 ? "442-Hole" : "—")}
                                                            </TableCell>
                                                            <TableCell className="text-sm">
                                                                {item.quantity}{" "}
                                                                {item.pricingMode === 2
                                                                    ? "Pcs"
                                                                    : item.unit === 11
                                                                    ? "Trays"
                                                                    : ""}
                                                            </TableCell>
                                                        </TableRow>
                                                    )
                                                )}
                                            </TableBody>
                                        </Table>
                                    </div>
                                </div>
                            )}
                        </>
                    )}

                    {/* Ready-Made Mode */}
                    {mode === "readyMade" && (
                        <div>
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-lg font-semibold">
                                    Sowing Instructions
                                </h3>
                                <Button
                                    onClick={handleAddReadyMadeForm}
                                    className="gap-2"
                                    disabled={
                                        createBatchMutation.isPending
                                    }
                                >
                                    <Plus className="w-4 h-4" />
                                    Add Instruction
                                </Button>
                            </div>
                        </div>
                    )}

                    {/* Sowing Instruction Forms */}
                    {selectedOrderItemIds.size > 0 && (
                        <div className="space-y-6 p-4 rounded border bg-muted/50">
                            {Array.from(selectedOrderItemIds).map((itemId) => {
                                const orderItem =
                                    mode === "customer"
                                        ? selectedOrder?.orderItems?.find(
                                            (item) => item.id === itemId
                                        )
                                        : undefined;
                                const form = sowingForms[itemId];

                                if (!form) return null;

                                return (
                                    <div
                                        key={`sowing-form-${itemId}`}
                                        className="relative"
                                    >
                                        {mode === "readyMade" && (
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="sm"
                                                className="absolute -top-2 -right-2 h-6 w-6 p-0"
                                                onClick={() =>
                                                    handleRemoveReadyMadeForm(
                                                        itemId
                                                    )
                                                }
                                                disabled={
                                                    createBatchMutation.isPending
                                                }
                                            >
                                                <X className="w-4 h-4" />
                                            </Button>
                                        )}
                                        <SowingFormItem
                                            itemId={itemId}
                                            errors={rowErrors[itemId]}
                                            orderItem={orderItem}
                                            form={form}
                                            isPending={
                                                createBatchMutation.isPending
                                            }
                                            onFormChange={handleFormChange}
                                            onGerminationSuggestion={handleGerminationSuggestion}
                                            onAddRequiredInput={
                                                handleAddRequiredInput
                                            }
                                            onRemoveRequiredInput={
                                                handleRemoveRequiredInput
                                            }
                                            onRequiredInputChange={
                                                handleRequiredInputChange
                                            }
                                        />
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {/* Action Buttons */}
                    <div className="flex gap-2 justify-end">
                        <Button
                            variant="secondary"
                            onClick={() => resetDialog()}
                            disabled={createBatchMutation.isPending}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleSubmit}
                            disabled={
                                createBatchMutation.isPending ||
                                selectedOrderItemIds.size === 0
                            }
                        >
                            {createBatchMutation.isPending
                                ? "Creating..."
                                : "Create Instructions"}
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};
